import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { pipe } from "fp-ts/function";
import * as O from "fp-ts/Option";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { LogInInput, LogInPayload, Viewer } from "src/generated/types";
import { SAVE_COOKIE_REQUEST } from "src/state/ducks/auth";
import { parseError } from "src/utils/parse_error";

/*
 * GraphQL
 */

export const LOGIN_MUTATION = gql`
    mutation Login($input: LogInInput!) {
        logIn(input: $input) {
            viewer {
                sessionToken
                user {
                    id
                    isAdmin
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloData {
    logIn: LogInPayload;
}

interface ApolloMutationVars {
    input: LogInInput;
}

interface FormValues {
    password: string;
    username: string;
}

/*
 * Main
 */

export const LoginForm = (): React.ReactElement => {
    const dispatch = useDispatch();
    const [login] = useMutation<ApolloData, ApolloMutationVars>(LOGIN_MUTATION);
    const [error, setError] = useState<O.Option<string>>(O.none);

    const initialValues: FormValues = {
        username: "",
        password: "",
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { setSubmitting }): void => {
                login({
                    variables: { input: { username: values.username, password: values.password } },
                })
                    .then(({ data }) => {
                        pipe(
                            O.fromNullable(data?.logIn.viewer),
                            O.fold(
                                () => {
                                    void 0;
                                },
                                (viewer: Viewer) => {
                                    dispatch({
                                        type: SAVE_COOKIE_REQUEST,
                                        payload: {
                                            isAdmin: viewer.user.isAdmin,
                                            token: viewer.sessionToken,
                                        },
                                    });
                                },
                            ),
                        );
                    })
                    .catch((error: ApolloError) => {
                        setError(O.some(parseError(error)));
                    });

                setSubmitting(false);
            }}
            validationSchema={Yup.object().shape({
                username: Yup.string().email("Invalid email address").required("Required"),
                password: Yup.string()
                    .matches(
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
                        "Must Contain 8 Characters, One Uppercase, One Lowercase",
                    )
                    .required("Required"),
            })}
        >
            {(formik: FormikProps<FormValues>) => (
                <Form>
                    {pipe(
                        error,
                        O.fold(
                            () => null,
                            (err) => <div className="form-error">{err}</div>,
                        ),
                    )}
                    <Input
                        autoComplete="username"
                        data-testid="username"
                        id="username"
                        name="username"
                        placeholder="Enter Your Email"
                    />
                    <Input
                        autoComplete="current-password"
                        data-testid="password"
                        id="password"
                        name="password"
                        placeholder="Enter Your Password"
                        type="password"
                    />
                    <Button disabled={formik.isSubmitting || !formik.isValid} type="submit" variation="secondary">
                        Sign In
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
